import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Security Interceptor for HTTP responses
 * Validates security headers on all responses and adds security-related request headers
 */
@Injectable()
export class SecurityResponseInterceptor implements HttpInterceptor {
  private readonly securityHeadersToCheck = [
    'content-type',
    'x-content-type-options',
    'x-frame-options',
    'x-xss-protection',
    'strict-transport-security',
  ];

  constructor() {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Add security-related request headers
    const secureRequest = req.clone({
      setHeaders: {
        // Prevent CSRF attacks by including custom header
        'X-Requested-With': 'XMLHttpRequest',
        // Ensure JSON content type for JSON requests
        ...(req.body && typeof req.body === 'object' && {
          'Content-Type': 'application/json',
        }),
      },
    });

    return next.handle(secureRequest).pipe(
      tap((event) => {
        // Validate security headers on successful responses
        if (event instanceof HttpResponse) {
          this.validateSecurityHeaders(event);
        }
      }),
    );
  }

  /**
   * Validate that response includes expected security headers
   * @param response - The HTTP response to validate
   */
  private validateSecurityHeaders(response: HttpResponse<any>): void {
    const missingHeaders: string[] = [];

    // Check for critical security headers (allow some to be missing for certain endpoints)
    const criticalHeaders = [
      'x-content-type-options',
      'x-frame-options',
      'strict-transport-security',
    ];

    for (const header of criticalHeaders) {
      if (!response.headers.has(header)) {
        missingHeaders.push(header);
      }
    }

    if (missingHeaders.length > 0) {
      // Log warning but don't fail - the server should have these in the response
      console.warn(
        `⚠️ Security: Missing expected security headers: ${missingHeaders.join(', ')}`,
      );
    }

    // Validate header values
    this.validateHeaderValues(response);
  }

  /**
   * Validate specific security header values
   * @param response - The HTTP response to validate
   */
  private validateHeaderValues(response: HttpResponse<any>): void {
    // Check X-Frame-Options
    const frameOptions = response.headers.get('x-frame-options');
    if (frameOptions && !['DENY', 'SAMEORIGIN'].includes(frameOptions.toUpperCase())) {
      console.warn(
        `⚠️ Security: X-Frame-Options header has unexpected value: ${frameOptions}`,
      );
    }

    // Check X-Content-Type-Options
    const contentTypeOptions = response.headers.get('x-content-type-options');
    if (
      contentTypeOptions &&
      contentTypeOptions.toLowerCase() !== 'nosniff'
    ) {
      console.warn(
        `⚠️ Security: X-Content-Type-Options header has unexpected value: ${contentTypeOptions}`,
      );
    }

    // Check for HSTS header
    const hsts = response.headers.get('strict-transport-security');
    if (hsts && !hsts.includes('max-age')) {
      console.warn(
        `⚠️ Security: Strict-Transport-Security header is malformed: ${hsts}`,
      );
    }

    // Check CSP header if present
    const csp = response.headers.get('content-security-policy');
    if (csp && !csp.includes("default-src 'none'")) {
      console.warn(
        `⚠️ Security: CSP policy should use default-src 'none', found: ${csp.substring(0, 50)}...`,
      );
    }
  }
}
